import { communicationInputSchema } from '@iculogbook/shared';
import type { CommunicationInput } from '@iculogbook/shared';
import { useLiveQuery } from 'dexie-react-hooks';
import { useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router';

import { useAuth } from '../auth/AuthProvider.tsx';
import { useToast } from '../components/index.ts';
import { communicationsRepository } from '../db/repositories/communications.ts';
import { CommunicationForm } from '../features/log/CommunicationForm.tsx';
import type { FormErrors } from '../features/log/ProcedureForm.tsx';
import {
  buildCommunicationInput,
  communicationValuesFrom,
  initialValues,
} from '../features/log/communicationForm.ts';
import type { CommunicationFormValues } from '../features/log/communicationForm.ts';

/**
 * Logging a Communication entry: a significant discussion, most often
 * withdrawal or withholding of treatment.
 *
 * Local, like every other write path in this app. The save writes to Dexie
 * and returns; the sync engine drains the outbox afterwards, whenever it can.
 */
function LogCommunicationRouteForm() {
  const { user } = useAuth();
  const { showToast } = useToast();
  const navigate = useNavigate();
  const { communicationId } = useParams();
  const location = useLocation();

  const userId = user?.id ?? null;
  const editing = communicationId !== undefined;

  /** A duplicate arrives prefilled through the router, never through the URL. */
  const prefill = (location.state as { prefill?: CommunicationInput } | null)?.prefill;

  const stored = useLiveQuery(
    async () =>
      userId === null || communicationId === undefined
        ? null
        : ((await communicationsRepository.get(userId, communicationId)) ?? null),
    [userId, communicationId],
  );

  const [values, setValues] = useState<CommunicationFormValues | null>(() =>
    editing
      ? null
      : prefill !== undefined
        ? communicationValuesFrom(prefill)
        : initialValues(new Date()),
  );
  const [errors, setErrors] = useState<FormErrors>({});
  const [saving, setSaving] = useState(false);

  /*
   * Seed once, then leave the form alone: a live query must never overwrite
   * something half-typed.
   */
  const [seeded, setSeeded] = useState(false);
  if (editing && !seeded && values === null && stored !== undefined && stored !== null) {
    setSeeded(true);
    setValues(communicationValuesFrom(stored));
  }

  async function save(andDuplicate = false): Promise<void> {
    if (values === null || userId === null) return;

    const input = buildCommunicationInput(values);
    if (input === null) {
      setErrors({
        ...(values.category === null
          ? { category: 'Choose what kind of discussion this was.' }
          : {}),
        ...(values.ageBand === null ? { ageBand: 'Choose an age band.' } : {}),
        ...(values.capabilityLevel === null
          ? { capabilityLevel: 'Choose a capability level.' }
          : {}),
      });
      return;
    }

    // The shared schema is the only validator. Anything it rejects is a bug in
    // the form rather than something the user did, but it is reported rather
    // than swallowed, because writing an invalid record would be worse.
    const parsed = communicationInputSchema.safeParse(input);
    if (!parsed.success) {
      const collected: FormErrors = {};
      for (const issue of parsed.error.issues) {
        const field = issue.path[0];
        if (typeof field === 'string' && collected[field] === undefined) {
          collected[field] = issue.message;
        }
      }
      setErrors(collected);
      return;
    }

    setSaving(true);

    try {
      let savedId = communicationId;

      if (editing && communicationId !== undefined) {
        await communicationsRepository.update(userId, communicationId, parsed.data);
        showToast({ message: 'Entry updated.', tone: 'success' });
      } else {
        const saved = await communicationsRepository.create(userId, parsed.data);
        savedId = saved.id;

        showToast({
          message: 'Entry saved.',
          tone: 'success',
          actions: [
            {
              label: 'Undo',
              onAction: () => {
                void communicationsRepository.softDelete(userId, saved.id);
                showToast({ message: 'Entry removed.' });
              },
            },
          ],
        });
      }

      const copy =
        andDuplicate && savedId !== undefined
          ? await communicationsRepository.duplicate(userId, savedId)
          : undefined;

      void navigate(copy === undefined ? '/entries' : '/log/communication', {
        ...(copy === undefined ? {} : { state: { prefill: copy } }),
      });
    } finally {
      setSaving(false);
    }
  }

  if (editing && stored === null) {
    return <p className="text-sm text-muted">That entry no longer exists.</p>;
  }

  if (values === null) return null;

  return (
    <CommunicationForm
      values={values}
      onChange={setValues}
      onSave={() => void save()}
      onSaveAndDuplicate={() => void save(true)}
      saving={saving}
      errors={errors}
      editing={editing}
    />
  );
}

/**
 * Saving and duplicating navigates to this same route with a fresh prefill.
 * Keying on the location gives that navigation a new instance, so the form
 * seeds from the copy instead of keeping what was typed into the last one.
 */
export function LogCommunicationRoute() {
  return <LogCommunicationRouteForm key={useLocation().key} />;
}
